<template>
  <div class="flex flex-wrap gap-2.5" role="radiogroup" :aria-label="t('label')">
    <button
      v-for="decision in DECISIONS"
      :key="decision"
      type="button"
      role="radio"
      :aria-checked="decision === selectedDecision"
      :tabindex="decision === focusableDecision ? 0 : -1"
      class="inline-flex items-center gap-2.5 rounded-lg border-2 bg-white px-3.5 py-2 text-sm font-semibold transition-colors"
      :class="
        decision === selectedDecision ? SELECTED_CLASSES[decision] : 'border-gray-200 text-gray-900 hover:bg-gray-50'
      "
      :data-test="`decision-${decision}`"
      :data-recommended="decision === recommendedDecision || null"
      @click="$emit('choose', decision)"
      @keydown="onKeydown($event, decision)"
    >
      <span
        class="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border"
        :class="decision === selectedDecision ? RADIO_CLASSES[decision] : 'border-gray-300'"
        aria-hidden="true"
      >
        <span v-if="decision === selectedDecision" class="h-2 w-2 rounded-full bg-current"></span>
      </span>
      <component :is="ICONS[decision]" class="h-4 w-4 shrink-0" :class="ICON_CLASSES[decision]" aria-hidden="true" />
      {{ t(`decision.${decision}`) }}
      <span
        v-if="decision === recommendedDecision"
        class="inline-flex items-center gap-0.5 rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-xs font-medium text-primary-dark"
        data-test="agent-badge"
      >
        <SparklesIcon class="h-3 w-3" aria-hidden="true" />
        {{ t("agent") }}
      </span>
    </button>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import { CheckIcon, ExclamationCircleIcon, SparklesIcon, XIcon } from "@heroicons/vue/outline";

export const DECISIONS = ["approve", "refuse", "complement"];

const ICONS = { approve: CheckIcon, refuse: XIcon, complement: ExclamationCircleIcon };

const ICON_CLASSES = {
  approve: "text-green-700",
  refuse: "text-red-600",
  complement: "text-yellow-600",
};

const SELECTED_CLASSES = {
  approve: "border-green-700 text-green-800",
  refuse: "border-red-600 text-red-700",
  complement: "border-yellow-500 text-yellow-800",
};

// Navigation clavier du motif ARIA radio : les flèches passent au choix voisin, en boucle.
const ARROW_STEPS = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

const RADIO_CLASSES = {
  approve: "border-green-700 text-green-700",
  refuse: "border-red-600 text-red-600",
  complement: "border-yellow-500 text-yellow-500",
};

export default {
  name: "ClaimDecisionChoices",

  components: { SparklesIcon },

  setup() {
    const { t } = useI18n();
    return { t, DECISIONS, ICONS, ICON_CLASSES, SELECTED_CLASSES, RADIO_CLASSES };
  },

  props: {
    // Décision dont le formulaire est ouvert ; nulle tant que l'ops n'a pas tranché.
    selectedDecision: {
      type: String,
      default: null,
    },
    // Verdict de l'agent général, marqué d'un badge. Nul tant que l'agent n'a pas rendu d'avis.
    recommendedDecision: {
      type: String,
      default: null,
    },
  },

  emits: ["choose"],

  computed: {
    // Un seul arrêt de tabulation pour le groupe : le choix coché, sinon le premier.
    focusableDecision() {
      return this.selectedDecision || DECISIONS[0];
    },
  },

  methods: {
    onKeydown(event, decision) {
      const step = ARROW_STEPS[event.key];
      if (!step) return;
      event.preventDefault();
      const index = (DECISIONS.indexOf(decision) + step + DECISIONS.length) % DECISIONS.length;
      event.currentTarget.parentElement.querySelectorAll("[role=radio]")[index].focus();
      this.$emit("choose", DECISIONS[index]);
    },
  },
};
</script>

<i18n src="./ClaimDecisionChoices.i18n.json"></i18n>
